import { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import dayjs from 'dayjs';
import { CalendarClock, X } from 'lucide-react';
import { OMS_COLORS } from '../../dashboard/omsDashTheme';
import { ADMIN_EYEBROW_CLASS, USAGE_COLOR } from '../adminTheme';
import { windowLabel, type ScopeWindow } from '../../dashboard/utils/scopeWindow';
import { formatNumber } from '../../dashboard/utils/formatNumber';
import type { AdminAnalytics } from '../../../../services/dashboardService';
import { DOW_LABELS, buildPeakMatrix, type EngineerMomentum } from '../utils/adminMetrics';
import { MiniSpark, UsageChip } from './adminBits';

interface Props {
  engineer: EngineerMomentum | null;
  payload: AdminAnalytics | null;
  window: ScopeWindow;
  phrase: string;
  onClose: () => void;
}

function StatTile({ label, value, ink }: { label: string; value: string; ink: string }) {
  return (
    <div className="rounded-xl border border-[rgba(11,59,46,0.18)] px-3 py-2" style={{ backgroundColor: '#F8F2E2' }}>
      <p className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: OMS_COLORS.caption }}>
        {label}
      </p>
      <p className="text-lg font-extrabold tabular-nums" style={{ color: ink }}>
        {value}
      </p>
    </div>
  );
}

/** Longest run of consecutive active days inside the window. */
function longestStreak(dates: string[]): number {
  const sorted = [...new Set(dates)].sort();
  let best = 0;
  let run = 0;
  let prev: dayjs.Dayjs | null = null;
  for (const date of sorted) {
    const d = dayjs(date);
    run = prev && d.diff(prev, 'day') === 1 ? run + 1 : 1;
    best = Math.max(best, run);
    prev = d;
  }
  return best;
}

/**
 * Engineer usage drawer — personal usage trend, personal peak-hours
 * mini-pulse, per-project task flow, onboarding date and active-day streak.
 */
export default function EngineerUsageDrawer({ engineer, payload, window, phrase, onClose }: Props) {
  const detail = useMemo(() => {
    if (!engineer || !payload) return null;
    const activity = payload.dailyActivity.filter((a) => a.engineerId === engineer.engineerId);
    const projectName = new Map(payload.projects.map((p) => [p.projectId, p.name]));
    const byProject = new Map<string, { name: string; actions: number; completed: number; skipped: number }>();
    for (const row of activity) {
      let entry = byProject.get(row.projectId);
      if (!entry) {
        entry = { name: projectName.get(row.projectId) ?? 'Unknown project', actions: 0, completed: 0, skipped: 0 };
        byProject.set(row.projectId, entry);
      }
      entry.actions += row.actions;
      entry.completed += row.completed;
      entry.skipped += row.skipped;
    }
    const peak = buildPeakMatrix(payload.peakByEngineer.filter((c) => c.engineerId === engineer.engineerId));
    return {
      projects: [...byProject.values()].sort((a, b) => b.actions - a.actions),
      peak,
      streak: longestStreak(activity.map((a) => a.date)),
    };
  }, [engineer, payload]);

  return (
    <AnimatePresence>
      {engineer && detail && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-[rgba(42,31,23,0.35)]"
            aria-hidden
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={`Usage detail for ${engineer.name}`}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-[rgba(11,59,46,0.28)] shadow-2xl"
            style={{ backgroundColor: '#F2EADB' }}
          >
            <div className="border-b border-[rgba(11,59,46,0.18)] px-5 py-4" style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={ADMIN_EYEBROW_CLASS}>Engineer · app usage</p>
                  <h2 className="mt-0.5 truncate text-xl font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                    {engineer.name}
                  </h2>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                    <span>{engineer.roleName || 'Site role'}</span>
                    <UsageChip usage={engineer.usage} />
                    <span className="font-extrabold tabular-nums" style={{ color: USAGE_COLOR[engineer.usage].ink }}>
                      {engineer.changePct === null ? '—' : `${engineer.changePct > 0 ? '+' : ''}${engineer.changePct}%`} {phrase}
                    </span>
                    {engineer.onboardedAt && (
                      <span className="flex items-center gap-1">
                        <CalendarClock className="h-3 w-3" />
                        onboarded {dayjs(engineer.onboardedAt).format('D MMM YYYY')}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close usage detail"
                  className="rounded-lg p-1.5 transition hover:bg-[rgba(11,59,46,0.08)]"
                  style={{ color: OMS_COLORS.tealDark }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: '#14606B' }}>
                {windowLabel(window)}
              </p>
            </div>

            <div className="sovereign-scrollbar flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatTile label="App actions" value={formatNumber(engineer.actions)} ink={OMS_COLORS.tealDark} />
                <StatTile label="Completed" value={formatNumber(engineer.completed)} ink="#1F5A47" />
                <StatTile label="Skipped" value={formatNumber(engineer.skipped)} ink={OMS_COLORS.slate} />
                <StatTile label="Best streak" value={`${detail.streak}d`} ink="#14606B" />
              </div>

              <section
                className="rounded-2xl border border-[rgba(11,59,46,0.22)] p-4"
                style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}
                aria-label="Weekly usage trend"
              >
                <p className={ADMIN_EYEBROW_CLASS}>Weekly app actions in window</p>
                <div className="mt-3 flex items-end gap-2">
                  <MiniSpark
                    values={engineer.weekly}
                    width={320}
                    height={56}
                    stroke={USAGE_COLOR[engineer.usage].fill}
                    label={`${engineer.name} weekly app actions trend`}
                  />
                  <span className="text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                    {engineer.weekly.map((v) => formatNumber(v)).join(' · ')}
                  </span>
                </div>
              </section>

              <section
                className="rounded-2xl border border-[rgba(11,59,46,0.22)] p-4"
                style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}
                aria-label="Personal peak hours"
              >
                <p className={ADMIN_EYEBROW_CLASS}>
                  Personal peak hours
                  {detail.peak.peak &&
                    ` · busiest ${DOW_LABELS[detail.peak.peak.dow - 1]} ${detail.peak.peak.hour}:00`}
                </p>
                {detail.peak.total === 0 ? (
                  <p className="py-4 text-sm" style={{ color: OMS_COLORS.caption }}>
                    No activity in this window.
                  </p>
                ) : (
                  <div className="mt-2 overflow-x-auto">
                    <div className="inline-block">
                      {detail.peak.cells.map((row, d) => (
                        <div key={d} className="flex items-center">
                          <span className="w-8 pr-1 text-right text-[8px] font-bold uppercase" style={{ color: OMS_COLORS.caption }}>
                            {DOW_LABELS[d]}
                          </span>
                          {row.map((value, h) => {
                            const text = `${DOW_LABELS[d]} ${h}:00 — ${formatNumber(value)} action${value === 1 ? '' : 's'}`;
                            const t = detail.peak.max > 0 ? value / detail.peak.max : 0;
                            return (
                              <span
                                key={h}
                                title={text}
                                aria-label={text}
                                className="m-[1px] rounded-[2px]"
                                style={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor:
                                    value === 0
                                      ? 'rgba(11,59,46,0.05)'
                                      : t < 0.34
                                        ? '#CFE3D9'
                                        : t < 0.67
                                          ? '#339274'
                                          : '#14606B',
                                }}
                              />
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section
                className="rounded-2xl border border-[rgba(11,59,46,0.22)] p-4"
                style={{ backgroundColor: 'rgba(250,247,238,0.96)' }}
                aria-label="Per-project task flow"
              >
                <p className={ADMIN_EYEBROW_CLASS}>Per-project flow · {detail.projects.length} projects</p>
                <ul className="mt-2 space-y-2">
                  {detail.projects.map((project) => (
                    <li key={project.name} className="flex items-baseline justify-between gap-2 text-[12px]">
                      <span className="min-w-0 truncate font-bold" style={{ color: OMS_COLORS.ink }}>
                        {project.name}
                      </span>
                      <span className="shrink-0 font-semibold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                        {formatNumber(project.actions)} actions · {formatNumber(project.completed)} completed ·{' '}
                        {formatNumber(project.skipped)} skipped
                      </span>
                    </li>
                  ))}
                  {detail.projects.length === 0 && (
                    <li className="py-3 text-center text-sm" style={{ color: OMS_COLORS.caption }}>
                      No project activity in this window.
                    </li>
                  )}
                </ul>
              </section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
